import React, { useRef, useEffect, useState } from "react";
import html2canvas from "html2canvas";
import { api } from "../../../services/api";
import { useLoadingMessages } from "../useLoadingMessages";
import LogoRefineOverlay from "./LogoRefineOverlay";

// T-shirt assets
import tshirtArcticWhiteFront from "../../../assets/t-shirts/Front/JC001-ARCTIC-WHITE-1776042201.webp";
import tshirtCharcoalFront from "../../../assets/t-shirts/Front/JC001-CHARCOAL-1776042221.webp";
import tshirtElectricPinkFront from "../../../assets/t-shirts/Front/JC001-ELECTRIC-PINK-1776042252.webp";
import tshirtHawaiianBlueFront from "../../../assets/t-shirts/Front/JC001-HAWAIIAN-BLUE-1776042275.webp";
import tshirtJadeFront from "../../../assets/t-shirts/Front/JC001-JADE-1776042296.webp";
import tshirtJetBlackFront from "../../../assets/t-shirts/Front/JC001-JET-BLACK-1776042300.webp";
import tshirtMagentaMagicFront from "../../../assets/t-shirts/Front/JC001-MAGENTA-MAGIC-1776042315.webp";
import tshirtOrangeCrushFront from "../../../assets/t-shirts/Front/JC001-ORANGE-CRUSH-1776042327.webp";
// @ts-ignore
import tshirtFrenchNavyFront from "../../../assets/t-shirts/Front/JC001J FRENCH NAVY (FRONT).webp";

// @ts-ignore
import tshirtArcticWhiteBack from "../../../assets/t-shirts/Back/JC001J ARCTIC WHITE (BACK).webp";
// @ts-ignore
import tshirtCharcoalBack from "../../../assets/t-shirts/Back/JC001J CHARCOAL (BACK).webp";
// @ts-ignore
import tshirtElectricPinkBack from "../../../assets/t-shirts/Back/JC001J ELECTRIC PINK (BACK).webp";
// @ts-ignore
import tshirtHawaiianBlueBack from "../../../assets/t-shirts/Back/JC001 HAWAIIIAN (BACK).webp";
// @ts-ignore
import tshirtJadeBack from "../../../assets/t-shirts/Back/JC001J JADE (BACK).webp";
// @ts-ignore
import tshirtJetBlackBack from "../../../assets/t-shirts/Back/JC001J JET BLACK (BACK).webp";
// @ts-ignore
import tshirtMagentaMagicBack from "../../../assets/t-shirts/Back/JC001J MAGENTA MAGIC (BACK).webp";
// @ts-ignore
import tshirtOrangeCrushBack from "../../../assets/t-shirts/Back/JC001 ORANGE CRUSH (BACK).webp";
// @ts-ignore
import tshirtFrenchNavyBack from "../../../assets/t-shirts/Back/JC001J FRENCH NAVY (BACK).webp";

// Polo assets
const poloWhiteFront = require("../../../assets/Polos/TR505_White_FRONTo.webp");
const poloRedFront = require("../../../assets/Polos/TR505_RED_FRONTo.webp");
const poloNavyFront = require("../../../assets/Polos/TR505_FrenchNavy_FRONTo.webp");
const poloBlackFront = require("../../../assets/Polos/TR505_Black_FRONTo.webp");
const poloWhiteBack = require("../../../assets/Polos/TR505_White_BACKo.webp");
const poloRedBack = require("../../../assets/Polos/TR505_FireRed_BACKo.webp");
const poloNavyBack = require("../../../assets/Polos/TR505_FrenchNavy_BACKo.webp");
const poloBlackBack = require("../../../assets/Polos/TR505_Black_BACKo.webp");
const frontPrintWhite = require("../../../assets/Logos/Tribelet Front Shirt Print White.png");
const frontPrintBlack = require("../../../assets/Logos/Tribelet Front Shirt Print Black.png");

const TSHIRT_COLORS = [
  { hex: "#F2F2F0", label: "Arctic White" },
  { hex: "#4A4A4A", label: "Charcoal" },
  { hex: "#FF1493", label: "Electric Pink" },
  { hex: "#0099CC", label: "Hawaiian Blue" },
  { hex: "#00A878", label: "Jade" },
  { hex: "#111111", label: "Jet Black" },
  { hex: "#CC00CC", label: "Magenta Magic" },
  { hex: "#FF5500", label: "Orange Crush" },
  { hex: "#1E3A8A", label: "French Navy" },
];

const POLO_COLORS = [
  { hex: "#FFFFFF", label: "White" },
  { hex: "#DC2626", label: "Red" },
  { hex: "#1E3A8A", label: "Navy" },
  { hex: "#000000", label: "Black" },
];

const TSHIRT_FRONT_MAP: Record<string, string> = {
  "#F2F2F0": tshirtArcticWhiteFront, "#4A4A4A": tshirtCharcoalFront,
  "#FF1493": tshirtElectricPinkFront, "#0099CC": tshirtHawaiianBlueFront,
  "#00A878": tshirtJadeFront, "#111111": tshirtJetBlackFront,
  "#CC00CC": tshirtMagentaMagicFront, "#FF5500": tshirtOrangeCrushFront,
  "#1E3A8A": tshirtFrenchNavyFront,
};

const TSHIRT_BACK_MAP: Record<string, string> = {
  "#F2F2F0": tshirtArcticWhiteBack, "#4A4A4A": tshirtCharcoalBack,
  "#FF1493": tshirtElectricPinkBack, "#0099CC": tshirtHawaiianBlueBack,
  "#00A878": tshirtJadeBack, "#111111": tshirtJetBlackBack,
  "#CC00CC": tshirtMagentaMagicBack, "#FF5500": tshirtOrangeCrushBack,
  "#1E3A8A": tshirtFrenchNavyBack,
};

const POLO_FRONT_MAP: Record<string, string> = {
  "#FFFFFF": poloWhiteFront, "#DC2626": poloRedFront,
  "#1E3A8A": poloNavyFront, "#000000": poloBlackFront,
};

const POLO_BACK_MAP: Record<string, string> = {
  "#FFFFFF": poloWhiteBack, "#DC2626": poloRedBack,
  "#1E3A8A": poloNavyBack, "#000000": poloBlackBack,
};

const imgSrc = (src: string) =>
  src.startsWith("data:") || src.startsWith("http") ? src : `data:image/png;base64,${src}`;

interface LogoCarouselWidgetProps {
  logoOptions: string[];
  carouselIndex: number;
  setCarouselIndex: (v: number) => void;
  setSelectedLogo: (v: string) => void;
  isGenerating: boolean;
  error: string;
  onOpenRefine: () => void;
}

const LogoCarouselWidget: React.FC<LogoCarouselWidgetProps> = ({
  logoOptions, carouselIndex, setCarouselIndex,
  setSelectedLogo,
  isGenerating, error,
  onOpenRefine,
}) => {
  const current = logoOptions[carouselIndex];
  const flick = (delta: number) => {
    if (logoOptions.length === 0) return;
    const next = (carouselIndex + delta + logoOptions.length) % logoOptions.length;
    setCarouselIndex(next);
    setSelectedLogo(logoOptions[next]);
  };

  return (
    <div className="bk-logo-carousel">
      {error && <div className="bk-error" style={{ marginRight: "0.5rem" }}>{error}</div>}

      <div
        className={`bk-logo-carousel-card${current ? " bk-logo-carousel-card--selected" : ""}`}
        onClick={() => current && onOpenRefine()}
      >
        {isGenerating ? (
          <div className="bk-logo-spinner" />
        ) : current ? (
          <img src={imgSrc(current)} alt={`Logo option ${carouselIndex + 1}`} />
        ) : null}
      </div>

      <div className="bk-logo-carousel-nav">
        <div className="bk-logo-carousel-arrows">
          <button className="bk-logo-carousel-arrow" onClick={() => flick(-1)} disabled={isGenerating || logoOptions.length < 2} aria-label="Previous logo">‹</button>
          <button className="bk-logo-carousel-arrow" onClick={() => flick(1)} disabled={isGenerating || logoOptions.length < 2} aria-label="Next logo">›</button>
        </div>
        <div className="bk-logo-carousel-dots">
          {logoOptions.map((_, i) => (
            <span key={i} className={`bk-logo-carousel-dot${i === carouselIndex ? " bk-logo-carousel-dot--active" : ""}`} />
          ))}
        </div>
      </div>
    </div>
  );
};

interface KitCreationStepProps {
  step: number;
  totalSteps: number;
  prompt: string;
  teamName: string;
  logoOptions: string[];
  setLogoOptions: (v: string[]) => void;
  selectedLogo: string;
  setSelectedLogo: (v: string) => void;
  logosLoading: boolean;
  kitType: string;
  setKitType: (v: string) => void;
  teamwearColor: string; setTeamwearColor: (v: string) => void;
  emblemColor: string; setEmblemColor: (v: string) => void;
  manualEmblemColor: boolean; setManualEmblemColor: (v: boolean) => void;
  showBackImage: boolean; setShowBackImage: (v: boolean) => void;
  backImageScale: number; setBackImageScale: (v: number) => void;
  designName: string; setDesignName: (v: string) => void;
  onPreviewReady: (designName: string, front: string, back: string) => void;
  onBack: () => void;
}

const KitCreationStep: React.FC<KitCreationStepProps> = ({
  step,
  totalSteps,
  prompt,
  teamName,
  logoOptions, setLogoOptions,
  selectedLogo, setSelectedLogo,
  logosLoading,
  kitType, setKitType,
  teamwearColor, setTeamwearColor,
  emblemColor, setEmblemColor,
  manualEmblemColor, setManualEmblemColor,
  showBackImage, setShowBackImage,
  backImageScale, setBackImageScale,
  designName, setDesignName,
  onPreviewReady, onBack,
}) => {
  // ── Logo carousel state ──
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [refineOverlayOpen, setRefineOverlayOpen] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoError, setLogoError] = useState("");
  const isGeneratingLogos = logoLoading || logosLoading;

  const generateLogos = async () => {
    setLogoLoading(true);
    setLogoError("");
    try {
      const context = prompt.trim() || teamName;
      const data = await api.generateLogo(context, teamName, 2);
      setLogoOptions(data.logo_images || []);
      if (!selectedLogo && data.logo_images?.length) {
        setSelectedLogo(data.logo_images[0]);
      }
    } catch {
      setLogoError("Logo generation failed. Please try again.");
    } finally {
      setLogoLoading(false);
    }
  };

  useEffect(() => {
    // Skip if already loaded or background prefetch is still in flight
    if (logoOptions.length === 0 && !logosLoading) generateLogos();
  }, []);

  useEffect(() => {
    if (carouselIndex >= logoOptions.length) setCarouselIndex(0);
  }, [logoOptions.length, carouselIndex]);

  const openRefine = () => setRefineOverlayOpen(true);

  // ── Kit design state (carried over from the former KitDesignStep) ──
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const [frontLoaded, setFrontLoaded] = useState(false);
  const [backLoaded, setBackLoaded] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const { message: captureMsg, animKey: captureKey } = useLoadingMessages(
    ["Capturing your kit…", "Rendering front view…", "Rendering back view…", "Preparing for checkout…"],
    capturing
  );

  const isPolo = kitType === "polo";
  const colorOptions = isPolo ? POLO_COLORS : TSHIRT_COLORS;

  const isLightColor = (hex: string) =>
    isPolo ? hex === "#FFFFFF" : hex === "#F2F2F0";

  useEffect(() => {
    setFrontLoaded(false);
    setBackLoaded(false);
  }, [teamwearColor, kitType]);

  const getFrontImg = () =>
    isPolo
      ? (POLO_FRONT_MAP[teamwearColor] || poloWhiteFront)
      : (TSHIRT_FRONT_MAP[teamwearColor] || tshirtArcticWhiteFront);

  const getBackImg = () =>
    isPolo
      ? (POLO_BACK_MAP[teamwearColor] || poloWhiteBack)
      : (TSHIRT_BACK_MAP[teamwearColor] || tshirtArcticWhiteBack);

  const getFrontPrint = () => {
    if (manualEmblemColor) return emblemColor === "#FFFFFF" ? frontPrintWhite : frontPrintBlack;
    return isLightColor(teamwearColor) ? frontPrintBlack : frontPrintWhite;
  };

  const getBackImageSrc = () => selectedLogo;

  const bothLoaded = frontLoaded && backLoaded;

  const handleContinue = async () => {
    if (!frontRef.current || !backRef.current) return;
    setCapturing(true);
    try {
      const capture = (el: HTMLElement) =>
        // @ts-ignore — backgroundColor: null is the correct html2canvas API for transparent output
        html2canvas(el, { backgroundColor: null, useCORS: true, allowTaint: true })
          .then((c: HTMLCanvasElement) => c.toDataURL("image/png"));
      const [frontUrl, backUrl] = await Promise.all([
        capture(frontRef.current),
        capture(backRef.current),
      ]);
      onPreviewReady(designName.trim() || "Untitled Design", frontUrl, backUrl);
    } catch (err) {
      console.error("Preview capture failed:", err);
      setCapturing(false);
    }
  };

  return (
    <div>
      <div className="bk-kit-header">
        <div className="bk-kit-header-title">
          <div className="bk-eyebrow">[ 02 / Kit ]</div>
          <h1 className="bk-title" style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)", marginBottom: "0" }}>
            Design your kit.
          </h1>
        </div>
      </div>

      <div className="bk-kit-designer">

        {/* LEFT — Controls */}
        <aside className="bk-kit-controls">

          {/* Progress */}
          <div style={{ marginBottom: "1.75rem" }}>
            <div className="bk-pip-row">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div key={i} className={`bk-pip ${i + 1 < step ? "bk-pip--done" : i + 1 === step ? "bk-pip--active" : ""}`} />
              ))}
            </div>
          </div>

          {/* Team name */}
          <div className="bk-ctrl-section">
            <label className="bk-ctrl-label">Team name</label>
            <div className="bk-name-pills">
              <button className="bk-name-pill bk-name-pill--selected" onClick={onBack} title="Change team name">
                {teamName}
              </button>
            </div>
          </div>

          {/* Generated logo options */}
          <div className="bk-ctrl-section">
            <LogoCarouselWidget
              logoOptions={logoOptions}
              carouselIndex={carouselIndex}
              setCarouselIndex={setCarouselIndex}
              setSelectedLogo={setSelectedLogo}
              isGenerating={isGeneratingLogos}
              error={logoError}
              onOpenRefine={openRefine}
            />
          </div>

          {/* Design name */}
          <div className="bk-ctrl-section">
            <label className="bk-ctrl-label">Design name</label>
            <input
              type="text"
              className="bk-ctrl-input"
              placeholder="e.g. Sunday League Away Kit"
              value={designName}
              onChange={e => setDesignName(e.target.value)}
              maxLength={60}
            />
          </div>

          {/* Teamwear colour */}
          <div className="bk-ctrl-section">
            <label className="bk-ctrl-label">Teamwear colour</label>
            <div className="bk-dot-swatches">
              {colorOptions.map(c => (
                <div
                  key={c.hex}
                  className={`bk-dot-swatch ${teamwearColor === c.hex ? "selected" : ""}`}
                  style={{ backgroundColor: c.hex }}
                  title={c.label}
                  onClick={() => {
                    setTeamwearColor(c.hex);
                    if (!manualEmblemColor) setEmblemColor(isLightColor(c.hex) ? "#000000" : "#FFFFFF");
                  }}
                />
              ))}
            </div>
          </div>

          {/* Emblem colour */}
          <div className="bk-ctrl-section">
            <label className="bk-ctrl-label">Emblem colour</label>
            <div className="bk-dot-swatches">
              <div
                className={`bk-dot-swatch ${emblemColor === "#FFFFFF" ? "selected" : ""}`}
                style={{ backgroundColor: "#FFFFFF" }}
                title="White"
                onClick={() => { setEmblemColor("#FFFFFF"); setManualEmblemColor(true); }}
              />
              <div
                className={`bk-dot-swatch ${emblemColor === "#000000" ? "selected" : ""}`}
                style={{ backgroundColor: "#000000" }}
                title="Black"
                onClick={() => { setEmblemColor("#000000"); setManualEmblemColor(true); }}
              />
            </div>
          </div>

          {/* Back image */}
          <div className="bk-ctrl-section">
            <label className="bk-ctrl-label">
              Back Image
              <span className="bk-ctrl-sublabel">Centre back</span>
            </label>
            {selectedLogo && (
              <label className="bk-checkbox-line">
                <input type="checkbox" checked={showBackImage} onChange={e => setShowBackImage(e.target.checked)} />
                <span className="bk-checkbox-text">Show team logo</span>
              </label>
            )}
            {getBackImageSrc() && showBackImage && (
              <div className="bk-slider-mini">
                <span className="bk-slider-mini-label">Scale</span>
                <input type="range" min="25" max="150" value={backImageScale} onChange={e => setBackImageScale(parseInt(e.target.value))} />
                <span className="bk-slider-mini-val">{backImageScale}%</span>
              </div>
            )}
          </div>

        </aside>

        {/* RIGHT — Garment preview */}
        <div className="bk-garment-stage">
          {!bothLoaded && (
            <div className="bk-garment-loading">
              <span className="bk-garment-loading-text">loading…</span>
            </div>
          )}

          <div className="bk-garment-pair">
            {/* FRONT */}
            <div className="bk-garment-view">
              <div className="bk-garment-view-label">Front</div>
              <div ref={frontRef} className="bk-garment-img-wrap">
                <img
                  src={getFrontImg()}
                  alt="Front"
                  className="bk-garment-img"
                  style={{ opacity: bothLoaded ? 1 : 0 }}
                  onLoad={() => setFrontLoaded(true)}
                />
                {bothLoaded && (
                  <>
                    <img src={getFrontPrint()} alt="Tribelet front print" style={{ position: "absolute", top: "50%", left: "50%", width: "34%", height: "auto", objectFit: "contain", transform: "translate(-50%, -50%)" }} />
                    {selectedLogo && (
                      <img src={imgSrc(selectedLogo)} alt="Team logo" style={{ position: "absolute", top: "28%", left: "62%", width: "15%", height: "auto", objectFit: "contain", transform: "translate(-50%, -50%)" }} />
                    )}
                  </>
                )}
              </div>
            </div>

            {/* BACK */}
            <div className="bk-garment-view">
              <div className="bk-garment-view-label">Back</div>
              <div ref={backRef} className="bk-garment-img-wrap">
                <img
                  src={getBackImg()}
                  alt="Back"
                  className="bk-garment-img"
                  style={{ opacity: bothLoaded ? 1 : 0 }}
                  onLoad={() => setBackLoaded(true)}
                />
                {bothLoaded && (
                  <>
                    {getBackImageSrc() && showBackImage && (
                      <img
                        src={imgSrc(getBackImageSrc())}
                        alt="Back image"
                        style={{ position: "absolute", top: "30%", left: "50%", width: `${backImageScale * 0.18}%`, height: "auto", objectFit: "contain", transform: `translate(-50%, -50%) scale(${backImageScale / 100})`, transformOrigin: "center" }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="bk-kit-actions">
            <button className="bk-btn-text" onClick={onBack} disabled={capturing}>← Back</button>
            <button className="bk-btn" onClick={handleContinue} disabled={!selectedLogo || !bothLoaded || capturing}>
              {capturing ? <span key={captureKey} className="bk-loading-msg">{captureMsg}</span> : "Continue →"}
            </button>
          </div>
        </div>

      </div>

      {refineOverlayOpen && (
        <LogoRefineOverlay
          selectedLogo={selectedLogo}
          onRefined={(newLogo) => {
            setSelectedLogo(newLogo);
            setLogoOptions(logoOptions.map((l, i) => i === carouselIndex ? newLogo : l));
          }}
          onClose={() => setRefineOverlayOpen(false)}
        />
      )}
    </div>
  );
};

export default KitCreationStep;
